<app-state-section [state]="sectionState">
  <app-dialog-layout class="bg-white">
    <ng-container dialog-title>{{ 'Location.AddLocation' | translate }}</ng-container>

    <form *ngIf="form" [formGroup]="form" class="mt-3">
      <div class="brd-t brd-b brd-300 py-3 flex column gap-3">
        <!-- Maps URL or coordinates — one field, `detectMapURL()` tells the two apart -->
        <div class="flex column">
          <label for="add-location-link" class="h6 m-0 weight-bold text-900">
            {{ "Location.Labels.MapUrlOrCoordinates" | translate }}
          </label>
          <div class="flex mt-2">
            <mat-form-field appearance="outline" class="d-block w-100 mr-2 radius-3">
              <input
                id="add-location-link"
                formControlName="link"
                matInput
                type="text"
                [placeholder]="'Location.Labels.MapUrlOrCoordinatesPlaceholder' | translate"
              />
              <!-- The validator names the failure; this is the only place an error is reported. -->
              <mat-error *ngIf="form.get('link').hasError('shortMapLink')">
                {{ "Location.Labels.ShortLinkNotSupported" | translate }}
              </mat-error>
              <mat-error *ngIf="form.get('link').hasError('invalidMapURL')">
                {{ "Location.Labels.UrlDetectError" | translate }}
              </mat-error>
              <mat-error *ngIf="form.get('link').hasError('invalidMapCoordinates')">
                {{ "Location.Labels.CoordinatesDetectError" | translate }}
              </mat-error>
            </mat-form-field>
            <button
              mat-flat-button
              type="button"
              class="radius-3 py-5 px-4 text-white bg-primary-200 weight-normal text-nowrap"
              (click)="detectMapURL()"
              [disabled]="isDetecting"
            >
              <mat-spinner *ngIf="isDetecting" diameter="16" class="d-inline-block mr-1"></mat-spinner>
              {{ 'Location.Labels.Detect' | translate }}
            </button>
          </div>

          <div *ngIf="urlLocationFound" class="add-location__location-found flex align-center justify-center mt-2 px-3 py-2 radius-2">
            <app-icon name="outline-tick-circle" class="text-success mr-2" size="sm"></app-icon>
            <span class="text-success small weight-medium">
              {{ 'Location.Labels.LocationFound' | translate }}: {{ latitude | number:'1.4-4' }}, {{ longitude | number:'1.4-4' }}
            </span>
          </div>

        </div>

        <!-- Divider -->
        <div class="add-location__divider flex align-center">
          <mat-divider class="bg-300"></mat-divider>
          <span class="small text-500 mx-3 text-nowrap">{{ 'Location.Labels.OrSearchLocation' | translate }}</span>
          <mat-divider class="bg-300"></mat-divider>
        </div>

        <!-- Place search -->
        <app-form-field-group>
          <app-autocomplete-google-maps
            formControlName="searchAddress"
            [placeholder]="'Placeholder.SearchLocation' | translate"
          ></app-autocomplete-google-maps>
        </app-form-field-group>

        <!-- Map with a draggable pin; clicking the map moves it too -->
        <div class="add-location__map pos-relative radius-2 brd brd-300" *ngIf="hasLocationPermission">
          <google-map
            *ngIf="googleMapLoaded && hasPin"
            [center]="{ lat: +latitude, lng: +longitude }"
            [zoom]="zoom"
            (mapClick)="onMapPointChanged($event)"
          >
            <map-marker
              [position]="{ lat: +latitude, lng: +longitude }"
              [options]="{ draggable: true }"
              (mapDragend)="onMapPointChanged($event)"
            ></map-marker>
          </google-map>

          <div class="add-location__map-hint bg-white brd brd-300 radius-2 px-2 py-1 small text-500 text-nowrap">
            {{ 'Location.Labels.DragPinHint' | translate }}
          </div>
        </div>

        <!-- Location name -->
        <div class="flex column gap-2">
          <label class="h6 m-0 weight-bold text-900 asterisk-after">{{ 'Location.Labels.LocationName' | translate }}</label>
          <app-form-field-group>
            <mat-form-field appearance="outline" class="w-100">
              <input
                matInput
                type="text"
                formControlName="name"
                [placeholder]="'Location.Labels.LocationNamePlaceholder' | translate"
              />
            </mat-form-field>
          </app-form-field-group>

          <mat-error class="small" *ngIf="form.get('name').touched && form.get('name').invalid">
            {{ 'ErrorMessages.Required' | translate }}
          </mat-error>
        </div>

        <!-- City — the create call rejects a location without one -->
        <div class="flex column gap-2">
          <label class="h6 m-0 weight-bold text-900 asterisk-after">{{ 'Location.Labels.City' | translate }}</label>
          <app-lazy-dropdown
            formControlName="city"
            [lookup]="LookupType.Cities"
            [placeholder]="'Placeholder.SelectCity' | translate"
            [lookupExtraParams]="cityParams"
          >
          </app-lazy-dropdown>

          <mat-error class="small" *ngIf="form.get('city').touched && form.get('city').invalid">
            {{ 'ErrorMessages.Required' | translate }}
          </mat-error>
        </div>

        <!-- Contact person — optional, collapsible -->
        <div class="flex column gap-2">
          <button
            mat-button
            type="button"
            class="add-location__contacts-toggle p-0 h-auto w-100 text-no-transform weight-normal"
            [attr.aria-expanded]="contactsExpanded"
            (click)="toggleContacts()"
          >
            <div class="flex align-center justify-space-between w-100">
              <div class="flex align-center gap-1">
                <span class="small weight-bold text-900">{{ 'AddShipment.ContactPerson' | translate }}</span>
                <span class="small text-300">{{ 'Providers.Trucks.Optional' | translate }}</span>
              </div>
              <app-icon [type]="IconType.MaterialDesign" [name]="contactsExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" size="xs" class="m-0 p-0"></app-icon>
            </div>
          </button>

          <div class="flex column gap-2" formArrayName="contacts" *ngIf="contactsExpanded">
            <div
              *ngFor="let row of contactsArray.controls; let i = index"
              [formGroupName]="i"
              class="flex gap-3 align-flex-start"
            >
              <div class="box grow-fixed">
                <app-form-field-group>
                  <mat-form-field appearance="outline" class="w-100">
                    <input matInput type="text" [placeholder]="'Inputs.FullName' | translate" formControlName="fullName" />
                  </mat-form-field>
                </app-form-field-group>

                <mat-error class="small mt-1" *ngIf="row.touched && row.hasError('incompleteContact')">
                  {{ 'ErrorMessages.Required' | translate }}
                </mat-error>
              </div>

              <div class="box grow-fixed">
                <app-phone-number-field
                  formControlName="phone"
                  [countries]="countries"
                  [required]="false"
                ></app-phone-number-field>
              </div>

              <div class="box nogrow" *ngIf="contactsArray.length > 1">
                <button
                  mat-icon-button
                  type="button"
                  class="bg-warn-lighter text-warn radius-2"
                  [attr.aria-label]="'Buttons.Delete' | translate"
                  (click)="removeContact(i)"
                >
                  <app-icon name="outline-minus" size="xs"></app-icon>
                </button>
              </div>
            </div>

            <!-- wrapper keeps the button at its content width, flush with the column above -->
            <div>
              <button mat-button type="button" class="p-0 h-auto text-accent weight-normal" (click)="addAnotherContact()">
                <span class="flex align-center gap-1">
                  <app-icon name="outline-add" size="sm" class="m-0 p-0"></app-icon>
                  <span>{{ 'AddShipment.AddAnotherContact' | translate }}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </form>

    <div dialog-action class="flex gap-3 justify-flex-end w-100 mt-2" *ngIf="form">
      <button mat-button type="button" class="bg-white brd brd-200 radius-2 text-800 px-3" (click)="cancel()">
        {{ 'Buttons.Cancel' | translate }}
      </button>
      <button
        mat-flat-button
        color="primary"
        type="button"
        class="radius-2 px-3"
        [disabled]="form.invalid || !hasPin"
        (click)="onConfirm()"
      >
        {{ 'Buttons.ConfirmLocation' | translate }}
      </button>
    </div>
  </app-dialog-layout>
</app-state-section>